import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";

const IMG_SOFA =
  "https://customer-assets-jai6qajn.emergentagent.net/job_mortgage-elite/artifacts/deemwlu9__91A7872.jpg";

const CHAPTERS = [
  {
    id: "chapter-01",
    number: "01",
    statement: "The bank is not the beginning.",
    body: "High-street lenders price risk they do not understand. We begin where their policies end — reading your income, your goals and your circumstances the way a private bank would, then taking your story to the lenders who will actually listen.",
    image: IMG_SOFA,
    alt: "Caitlin Morrison reviewing a loan application",
  },
  {
    id: "chapter-02",
    number: "02",
    statement: "Structure before price.",
    body: "A rate is a number; a structure is a strategy. Across dozens of lenders, we shape facilities that protect your cash flow, anticipate your next move, and stay elegant long after settlement day.",
    image: null,
    alt: null,
  },
  {
    id: "chapter-03",
    number: "03",
    statement: "Care as standard.",
    body: "Every enquiry is answered personally. Every conversation is held in confidence, every detail treated as if it were our own name on the contract — because your home deserves nothing less.",
    image: null,
    alt: null,
  },
];

const ChapterImage = ({ chapter }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  if (!chapter.image) return null;
  return (
    <Reveal delay={0.2} className="md:col-span-3 md:col-start-10 md:mt-24">
      <div
        ref={ref}
        className="overflow-hidden rounded-[4px] aspect-[4/5] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.1)]"
        data-testid={`${chapter.id}-image-frame`}
      >
        <motion.img
          src={chapter.image}
          alt={chapter.alt}
          style={{ y, scale: 1.2 }}
          className="w-full h-full object-cover"
          data-testid={`${chapter.id}-image`}
        />
      </div>
    </Reveal>
  );
};

export const Manifesto = () => (
  <section
    id="philosophy"
    data-testid="manifesto-section"
    className="relative py-32 md:py-48"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <p
          data-testid="manifesto-overline"
          className="text-xs font-semibold tracking-[0.35em] uppercase text-[#CFAEA5] mb-24 md:mb-36"
        >
          The Mont Elk Philosophy
        </p>
      </Reveal>
      <div className="space-y-40 md:space-y-56">
        {CHAPTERS.map((chapter) => (
          <div
            key={chapter.id}
            data-testid={chapter.id}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start"
          >
            <div className="md:col-span-2">
              <span
                data-testid={`${chapter.id}-number`}
                className="font-display text-7xl md:text-8xl font-medium text-[#493E38]/[0.08] md:sticky md:top-40 block"
              >
                {chapter.number}
              </span>
            </div>
            <div className="md:col-span-6 md:col-start-4">
              <Reveal>
                <h2
                  data-testid={`${chapter.id}-statement`}
                  className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-balance"
                >
                  {chapter.statement}
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p
                  data-testid={`${chapter.id}-body`}
                  className="mt-8 max-w-lg text-base leading-relaxed tracking-wide text-[#908B85]"
                >
                  {chapter.body}
                </p>
              </Reveal>
            </div>
            <ChapterImage chapter={chapter} />
          </div>
        ))}
      </div>
    </div>
  </section>
);
